import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';
import { TriggerType } from 'components/alerts/SaveAlertDialog';
import SearchActionsDropdown, { SearchActions } from 'components/search/SearchActionsDropdown';
import { CopyToClipboardHandler, DownloadFileHandler } from '../../PowerQueriesPage';

type PowerQueryActionsDropdownProps = {
  copyToClipboardHandler: CopyToClipboardHandler;
  downloadFileHandler: DownloadFileHandler;
  disableCopyAndDownload: boolean;
};

const PowerQueryActionsDropdown = ({
  copyToClipboardHandler,
  downloadFileHandler,
  disableCopyAndDownload,
}: PowerQueryActionsDropdownProps): JSX.Element => {
  const isPQAlertsLabEnabled = useIsLabEnabled(K.LAB.PQ_ALERTS);

  const saveSection = [SearchActions.SAVE, SearchActions.DASHBOARD];

  if (isPQAlertsLabEnabled) {
    saveSection.push(SearchActions.ALERT);
  }

  const actionMenuSections = [
    saveSection,
    [
      SearchActions.COPY_LINK,
      {
        text: 'Copy to Clipboard',
        key: 'copy_to_clipboard',
        clickHandler: () => {},
        disabled: disableCopyAndDownload,
        subMenuItems: [
          {
            text: 'CSV',
            key: K.DATA_FORMAT.CSV,
            clickHandler: () => copyToClipboardHandler(K.DATA_FORMAT.CSV, 'CSV'),
          },
          {
            text: 'Text',
            key: K.DATA_FORMAT.TEXT,
            clickHandler: () => copyToClipboardHandler(K.DATA_FORMAT.TEXT, 'table', true),
          },
          {
            text: 'JSON',
            key: K.DATA_FORMAT.JSON,
            clickHandler: () => copyToClipboardHandler(K.DATA_FORMAT.JSON, 'JSON'),
          },
          {
            text: 'Markdown',
            key: K.DATA_FORMAT.MARKDOWN,
            clickHandler: () => copyToClipboardHandler(K.DATA_FORMAT.MARKDOWN, 'markdown table', true),
          },
        ],
        testId: ELEMENT.powerQueryCopyToClipboard,
      },
      {
        text: 'Download',
        key: 'download',
        clickHandler: () => {},
        disabled: disableCopyAndDownload,
        subMenuItems: [
          {
            text: 'CSV',
            key: K.DATA_FORMAT.CSV,
            clickHandler: () => downloadFileHandler(K.DATA_FORMAT.CSV),
          },
          {
            text: 'Text',
            key: K.DATA_FORMAT.TEXT,
            clickHandler: () => downloadFileHandler(K.DATA_FORMAT.TEXT, true),
          },
          {
            text: 'JSON',
            key: K.DATA_FORMAT.JSON,
            clickHandler: () => downloadFileHandler(K.DATA_FORMAT.JSON),
          },
          {
            text: 'Markdown',
            key: K.DATA_FORMAT.MARKDOWN,
            clickHandler: () => downloadFileHandler(K.DATA_FORMAT.MARKDOWN, true),
          },
        ],
        testId: ELEMENT.powerQueryDownload,
      },
    ],
    [
      {
        text: 'PowerQueries Help',
        href: HELP_ROUTE.HELP_POWER_QUERIES,
        openInNewTab: true,
        testId: ELEMENT.powerQueryHelp,
      },
    ],
  ];

  return (
    <SearchActionsDropdown
      text="Actions"
      sections={actionMenuSections}
      buttonSize="sm"
      testId={ELEMENT.powerQuery}
      buttonTestId={ELEMENT.powerQueryActionsButton}
      shouldFetchShortUrlAfterOpen
      saveAlertDialogConfig={{ triggerType: TriggerType.TABLE }}
      downloadDisabled={disableCopyAndDownload}
      alignRight
    />
  );
};

export default PowerQueryActionsDropdown;
